import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import smartDeviceService from '../../../services/SmartDeviceService';
import plugVueCache from './PlugVueCache';
import plugVueFetcher from './PlugVueFetcher';
import plugPinStore from './PlugPinStore';
import plugVueSectionBuilder from './PlugVueSectionBuilder';
import { PV_OPS, PV_STATUS, PV_TARGET_ALL } from './plugVueConst';

const _name = 'PlugVueEngine';

const LIVE_DEFAULTS = { power: 0, amp: null, powered: false, online: false, ems: null };

// FE Engine Pattern V2 — static settings (fetch) + live plug state (OnPlugStateChanged)
class PlugVueEngine extends WssEventHandlerBase {
    _name = _name;
    _sections = [];

    // no this.clear() here — boot-fetch may already have filled the cache
    bootup() {
        super.bootup();
        this._unsubscribe();
        this._unsub = this._wsNotifier.subscribe(events.OnPlugStateChanged, this.onMessage);
        this._unsubSettings = this._wsNotifier.subscribe(events.OnDeviceSettingsUpdated, this.onSettingsUpdated);
    }

    clear() {
        super.clear();
        this._unsubscribe();
        plugVueCache.clear();
    }

    _unsubscribe = () => {
        if (this._unsub) this._unsub();
        if (this._unsubSettings) this._unsubSettings();
        this._unsub = this._unsubSettings = null;
    };

    fetch = async () => {
        try {
            const rows = await plugVueFetcher.fetch();
            rows.forEach((r) => this._merge(r));
            this._notify(null);
            return rows;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * Loads the section names (BE order) used for layout and the /plugs dropdown.
     * @returns {Promise<string[]>}
     */
    fetchSections = async () => {
        try {
            this._sections = await plugVueFetcher.fetchSections();
            this._notify(null);
            return this._sections;
        } catch (error) {
            this.logError('fetchSections', error.message);
            return [];
        }
    };

    getSections = () => this._sections;

    /**
     * @param {{rk: string, power: number, amp: number, powered: boolean, online: boolean, ems: number}[]} items
     */
    onMessage = (items) => {
        try {
            if (!Array.isArray(items)) return;
            items.forEach((dto) => this._mergeLive(dto));
            this._notify(null);
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    // live state only lands on a known settings row — unknown rk has no alias/name
    _mergeLive = (dto) => {
        if (!plugVueCache.getKey(dto.rk)) return;
        this._merge(dto);
    };

    /**
     * Settings changed in BE (RMQ → SignalR) — same shape as getSettings: {items:[...]}, an array, or one item.
     * Live fields on the cached row stay untouched.
     * @param {object|object[]} payload
     */
    onSettingsUpdated = (payload) => {
        try {
            const items = Array.isArray(payload) ? payload : (payload?.items ?? (payload?.rk ? [payload] : []));
            items.map(plugVueFetcher._map).forEach((r) => this._merge(r));
            this._notify(null);
        } catch (error) {
            this.logError('onSettingsUpdated', error.message);
        }
    };

    getAll = () => plugVueCache.getAll();

    getPlug = (rk) => plugVueCache.getKey(rk);

    /**
     * @param {boolean} drawing - hide plugs at or below the drawing threshold
     * @param {string} target - PV_TARGET_ALL or a single section name
     * @returns {{pinned: object[], sections: object[], totalW: number}} display model for one widget instance
     */
    getView = (drawing, target = PV_TARGET_ALL) => {
        const rows = plugVueCache.getAll();
        const isAll = target === PV_TARGET_ALL;
        const sections = plugVueSectionBuilder.build(rows, drawing, this._sections, isAll ? null : target);
        return {
            pinned: isAll ? plugVueSectionBuilder.pinned(rows, drawing) : [],
            sections,
            totalW: isAll ? plugVueSectionBuilder.totalW(rows) : sections.reduce((sum, s) => sum + s.totalW, 0),
        };
    };

    togglePin = (rk) => {
        const row = plugVueCache.getKey(rk);
        if (!row) return;
        plugVueCache.setKey(rk, { ...row, pinned: plugPinStore.toggle(rk) });
        this._notify(rk);
    };

    setSwitch = (rk, on) => {
        const value = on ? 'true' : 'false';

        this._command(rk, 'SetSwitch', value);
    };

    setLed = (rk, on) => this._command(rk, PV_OPS.led, on);

    /**
     * Posts edited settings for one plug — the cache updates via OnDeviceSettingsUpdated.
     * @param {object} row - cached plug row merged with the edited fields
     * @returns {Promise<boolean>} true on success
     */
    saveSettings = async (row) => {
        try {
            await smartDeviceService.postSettings({
                '@desc': `Update plug ${row.name} (${row.alias})`,
                rk: row.rk,
                name: row.name,
                alias: row.alias,
                mac: row.mac,
                ip: row.ip,
                deviceClass: row.deviceClass,
                gname: row.gname ?? '',
                section: row.section ?? '',
                parentRK: row.parentRk ?? '',
                battery: row.battery ?? '',
                description: row.description ?? '',
                active: !!row.active,
            });
            return true;
        } catch (error) {
            this.logError('saveSettings', `${row.rk}: ${error.message}`);
            return false;
        }
    };

    _command = async (rk, operation, value) => {
        try {
            const row = plugVueCache.getKey(rk);
            if (!row) return;

            const dto = {
                deviceName: rk,
                operation: operation,
                parameter: value,
            };

            await smartDeviceService.postDeviceCommand(dto);
        } catch (error) {
            this.logError('_command', `${operation} ${rk}: ${error.message}`);
        }
    };

    // setKey replaces — merge live/static here, pinned comes from the store once
    _merge = (part) => {
        const row = { ...LIVE_DEFAULTS, ...plugVueCache.getKey(part.rk), ...part };
        row.pinned = row.pinned ?? plugPinStore.has(row.rk);
        row.status = this._resolveStatus(row);
        plugVueCache.setKey(row.rk, row);
    };

    _resolveStatus = (row) => {
        if (!row.online) return PV_STATUS.offline;
        return row.powered ? PV_STATUS.on : PV_STATUS.off;
    };

    // notify carries only the rk — widget reads back from the engine
    _notify = (rk) => {
        ComponentNotifier.notify(_name, 'dataChanged', { rk });
    };
}

const plugVueEngine = new PlugVueEngine();
export default plugVueEngine;
